import { BaseResponse } from '@/types/common';
import axiosInstance from './axiosInstance';
import { AxiosResponse } from 'axios';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

export interface ExhibitionListParams {
  title?: string;
  status?: number;
  currentPage: number;
  pageSize: number;
}

export enum ExhibitionFeeStatus {
  Free = 0,
  Paid = 1,
}

export enum ExhibitionStatus {
  Failed = -1, // 图片上传失败
  Pending = 0, // 初始状态
  Approved = 1, // 上架
  Rejected = 2, // 下架
  Draft, // 草稿
}

interface ExhibitionImage {
  imageName: string;
  imageUrl: string;
  ratio: number;
}

export interface Exhibition {
  id: number;
  title: string;
  feeStatus: ExhibitionFeeStatus;
  status: ExhibitionStatus;
  updateTime: string;
  exhibitionImageList: ExhibitionImage[];
}

export interface ExhibitionListResponse {
  records: Exhibition[];
  total: number;
}

export const getExhibitionList = async (params: ExhibitionListParams) => {
  const resp = await axiosInstance.post<BaseResponse<ExhibitionListResponse>>(
    `${baseUrl}/exhibition/list`,
    params
  );
  return resp.data;
};

export interface ExhibitionFormData {
  title: string;
  description?: string;
  venue: string;
  startDateStr: string;
  endDateStr: string;
  hours: string;
  closeTime?: string;
  fee?: string;
  website?: string;
  location: string;
  access?: string;
  phone?: string;
  imageUrl?: string;
  ratio?: number;
  postalCode: string;
  extData?: ExhibitionContent[];
  venuePhoto?: string;
  venuePhotoRatio?: number;
  latitude: string | number;
  longitude: string | number;
  road: string;
  hall: string;
  city: string;
  village: string;
}

export const createExhibition = async (data: ExhibitionFormData) => {
  const response = await axiosInstance.post<BaseResponse<string>>(
    `${baseUrl}/exhibition/add`,
    {
      ...data,
      extData: JSON.stringify(data.extData),
    }
  );
  return response.data;
};

export interface ExhibitionDetailData
  extends Omit<ExhibitionFormData, 'files' | 'extData'> {
  id: number;
  exhibitionImageList: ExhibitionImage[];
  extData: string;
}

export const getExhibitionDetail = async (id: number) => {
  const resp = await axiosInstance.get<BaseResponse<ExhibitionDetailData>>(
    `${baseUrl}/exhibition/detail/${id}`
  );
  return resp.data;
};

export interface ExhibitionUpdateData extends ExhibitionFormData {
  id: number;
}

export const updateExhibition = async (data: ExhibitionUpdateData) => {
  const resp = await axiosInstance.post<BaseResponse<void>>(
    `${baseUrl}/exhibition/edit`,
    {
      ...data,
      extData: JSON.stringify(data.extData),
    }
  );
  return resp.data;
};

export const onlineExhibition = async (id: number) => {
  const resp = await axiosInstance.post<BaseResponse<void>>(
    `${baseUrl}/exhibition/online`,
    { id }
  );
  return resp.data;
};

export const offlineExhibition = async (id: number) => {
  const resp = await axiosInstance.post<BaseResponse<void>>(
    `${baseUrl}/exhibition/offline`,
    { id }
  );
  return resp.data;
};

export const deleteExhibition = async (ids: string) => {
  const resp = await axiosInstance.post<BaseResponse<void>>(
    `${baseUrl}/exhibition/delete`,
    { ids }
  );
  return resp.data;
};

export interface ExhibitionContent {
  imageUrl: string;
  ratio: number;
  deltaContent: string;
  htmlContent: string;
}

interface ExhibitionCommentParamDTO {
  id: number;
  // violationStatus: number;
  // deleteStatus: number;
}

interface ExhibitionCommentViolationStatusParamDTO
  extends ExhibitionCommentParamDTO {
  violationStatus: number;
}

export interface ResponseDTO {
  status: number;
  success: boolean;
  message: string;
  data: object;
}

export interface ExhibitionCommentQueryDTO {
  currentPage: number;
  pageSize: number;
  // violationStatus: number;
  deleteStatus?: number;
  // suppNumber: number;
  // exhibitionId: number;
}

export interface ResponseDTOPageQueryResultExhibitionCommentDTO {
  status: number;
  success: boolean;
  message: string;
  data: PageQueryResultExhibitionCommentDTO;
}

export interface PageQueryResultExhibitionCommentDTO {
  total: number;
  currentPage: number;
  pageSize: number;
  records: ExhibitionCommentDTO[];
}

export interface ExhibitionCommentDTO {
  id: number;
  exhibitionId: number;
  exhibitionTitle: string;
  score: number;
  content: string;
  createTime: string;
  updateTime: string;
  userName: string;
  userId: number;
  userNumber: string;
  violationStatus: number;
  deleteStatus: number;
}

/**
 * 评论确认违规/解除违规
 * @param {*} params
 * @returns
 */
export const commentViolation = async (
  params: ExhibitionCommentViolationStatusParamDTO
): Promise<AxiosResponse<ResponseDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/exhibition/comment/violation`,
    params
  );

  return response;
};

/**
 * 评论列表
 * @param {*} params
 * @returns
 */
export const getExhibitionCommentList = async (
  params: ExhibitionCommentQueryDTO
): Promise<AxiosResponse<ResponseDTOPageQueryResultExhibitionCommentDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/exhibition/comment/list`,
    params
  );

  return response;
};

/**
 * 删除评论
 * @param {*} params
 * @returns
 */
export const commentDelete = async (
  params: ExhibitionCommentParamDTO
): Promise<AxiosResponse<ResponseDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/exhibition/comment/delete`,
    params
  );

  return response;
};

/**
 * 获取评论数量
 * @param {*} params
 * @returns
 */
export const getCommentCount = async (): Promise<
  AxiosResponse<ResponseDTO>
> => {
  const response = await axiosInstance.get(
    `${baseUrl}/exhibition/comment/count`,
    {}
  );

  return response;
};